import React, { useState, useEffect, useRef } from "react";
import { Theme } from "../Theme.js";
import { useProgress } from "../Context/ProgressContextClean.js";

/* Feed effects intentionally control their own storage and refresh lifecycle. */
/* eslint-disable react-hooks/exhaustive-deps */
import { generateClient } from "aws-amplify/api";
import { fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import {
  listGeneralInformations,
  listNotifications,
} from "../graphql/queries.js";
import { createNotifications } from "../graphql/mutations.js";

/* =========================================================
   CONFIG
========================================================= */

export const UPLIFT_EMOJIS = [
  { emoji: "👏", label: "Applaud effort" },
  { emoji: "🤍", label: "Show care" },
  { emoji: "💡", label: "Insightful" },
];

const client = generateClient();

const REPORT_TYPES = {
  UNSAFE: "CommunityPostUnsafe",
  HIDE_PERSONAL: "CommunityPostHidePersonal",
  HIDE_OFFENSIVE: "CommunityPostHideOffensive",
};

const NOTIFICATION_SENDER = {
  id: "community-moderation-system",
  firstName: "Community",
  lastName: "Moderation",
  email: "noreply@outcomesexcellence.org",
};

function parseSeasonSnapshot(value) {
  if (!value) return null;
  try {
    const snapshot = typeof value === "string" ? JSON.parse(value) : value;
    return snapshot?.type === "season-tree" ? snapshot : null;
  } catch {
    return null;
  }
}

function SeasonTreeSnapshot({ snapshot }) {
  const growth = snapshot?.growth || {};
  const milestones = Array.isArray(snapshot?.milestones)
    ? snapshot.milestones
    : [];
  const totalSignals =
    Number(growth.leaves || 0) +
    Number(growth.flowers || 0) +
    Number(growth.fruit || 0);
  const canopyScale = Math.min(1.2, 0.78 + totalSignals / 180);
  const seasonColor = snapshot.seasonColor || "#2F5D50";
  const milestonePositions = [
    { top: -12, left: "12%" },
    { top: -16, right: "14%" },
    { bottom: -6, left: "44%", transform: "translateX(-50%)" },
    { bottom: -8, right: "20%" },
    { top: "12%", left: "52%" },
    { top: "42%", left: "8%" },
    { top: -8, left: "50%", transform: "translateX(-50%)" },
  ];
  const signalNodes = (count, emoji, seed) =>
    Array.from({ length: Math.min(count, 10) }, (_, index) => ({
      emoji,
      left: `${10 + ((seed + index * 29) % 78)}%`,
      top: `${12 + ((seed * 3 + index * 17) % 55)}%`,
    }));
  const nodes = [
    ...signalNodes(Number(growth.leaves || 0), "🍃", 11),
    ...signalNodes(Number(growth.flowers || 0), "🌸", 37),
    ...signalNodes(Number(growth.fruit || 0), "🍊", 61),
  ];

  return (
    <section
      style={{
        border: `1px solid ${seasonColor}`,
        borderRadius: 8,
        padding: 14,
        textAlign: "center",
        background: "#FAFCFA",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 10 }}>
        {snapshot.seasonName} Season Tree
      </div>
      <div
        style={{
          height: 180,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 8px",
        }}
        aria-label={`${snapshot.seasonName} season tree`}
      >
        <div
          style={{
            position: "relative",
            width: 140,
            height: 110,
            borderRadius: "70px",
            transform: `scale(${canopyScale})`,
            background: `radial-gradient(circle at 30% 25%, #ffffff, ${seasonColor})`,
            boxShadow: `0 0 18px rgba(255,255,255,0.45), 0 0 28px ${seasonColor}66`,
          }}
        >
          {nodes.map((node, index) => (
            <span
              key={`${node.emoji}-${index}`}
              style={{
                position: "absolute",
                left: node.left,
                top: node.top,
                fontSize: node.emoji === "🍃" ? 15 : 17,
              }}
            >
              {node.emoji}
            </span>
          ))}
          {milestones.map((milestone, index) => (
            <span
              key={`${milestone.icon}-${milestone.label}`}
              title={milestone.label}
              style={{
                position: "absolute",
                fontSize: index === 6 ? 18 : 16,
                ...milestonePositions[index],
              }}
            >
              {milestone.icon}
            </span>
          ))}
        </div>
        <div
          style={{
            width: 22,
            height: 55,
            background: "#8B5A2B",
            borderRadius: 8,
            marginTop: -6,
          }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 14 }}>
        <span>🍃 {Number(growth.leaves || 0)}</span>
        <span>🌸 {Number(growth.flowers || 0)}</span>
        <span>🍊 {Number(growth.fruit || 0)}</span>
      </div>
      <div style={{ marginTop: 8, color: Theme.colors.muted, fontSize: 12 }}>
        Shared snapshot
      </div>
    </section>
  );
}

/* =========================================================
   COMPONENT
========================================================= */

export default function CommunityActivity({
  currentUserID: propCurrentUserID,
}) {
  const {
    activities = [],
    joinedCommunities = [],
    createActivity,
    giveUplift,
    updateActivity,
    deleteActivity,
    refresh,
    uplifts = [],
    loading: progressLoading,
    currentUserID: ctxCurrentUserID,
  } = useProgress();

  // viewerId: prefer current user from context, then explicit prop, then infer
  const viewerId =
    ctxCurrentUserID ||
    propCurrentUserID ||
    (activities && activities.length ? activities[0].userID : null);

  const [viewerAliases, setViewerAliases] = useState([]);

  function normalizeId(value) {
    if (value === null || value === undefined) return "";
    return String(value).trim().toLowerCase();
  }

  function isUsableAlias(value) {
    const n = normalizeId(value);
    return !!n && n !== "undefined" && n !== "null" && n !== "nan";
  }

  function isViewer(value) {
    const n = normalizeId(value);
    if (!n) return false;
    const aliasSet = new Set(
      [viewerId, ...(viewerAliases || [])].map(normalizeId).filter(Boolean),
    );
    return aliasSet.has(n);
  }

  useEffect(() => {
    let cancelled = false;
    async function loadAliases() {
      try {
        const user = await getCurrentUser();
        if (cancelled) return;
        const aliases = [
          viewerId,
          user?.userId,
          user?.username,
          user?.attributes?.sub,
          user?.signInDetails?.loginId,
        ].filter(Boolean);
        setViewerAliases(Array.from(new Set(aliases)));
      } catch {
        if (!cancelled) setViewerAliases(viewerId ? [viewerId] : []);
      }
    }
    loadAliases();
    return () => {
      cancelled = true;
    };
  }, [viewerId]);

  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState("PUBLIC");
  const [selectedCommunity, setSelectedCommunity] = useState("ALL");
  const [isPosting, setIsPosting] = useState(false);
  const [localActivities, setLocalActivities] = useState([]);
  const [bouncing, setBouncing] = useState({});
  const [editingPostId, setEditingPostId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [editingVisibility, setEditingVisibility] = useState("PUBLIC");
  const [hiddenPostIDs, setHiddenPostIDs] = useState([]);
  const [activeReportPostID, setActiveReportPostID] = useState(null);
  const [reportingPostID, setReportingPostID] = useState(null);
  const [reportFeedback, setReportFeedback] = useState("");
  const bootstrapRefreshAttempts = useRef(0);

  function getHiddenPostsStorageKey() {
    const id = normalizeId(viewerId) || "anon";
    return `community_hidden_posts_${id}`;
  }

  function persistHiddenPosts(nextIDs) {
    try {
      localStorage.setItem(getHiddenPostsStorageKey(), JSON.stringify(nextIDs));
    } catch (e) {
      /* ignore */
    }
  }

  function hidePostLocally(postID) {
    setHiddenPostIDs((prev) => {
      const set = new Set(prev || []);
      set.add(String(postID));
      const next = Array.from(set);
      persistHiddenPosts(next);
      return next;
    });
  }

  function isPostHidden(postID) {
    return (hiddenPostIDs || []).includes(String(postID));
  }

  // merge persisted selectedCommunityMap as a fallback (one-time)
  const persistedMap = (() => {
    try {
      const raw = localStorage.getItem("selectedCommunityMap");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  })();

  useEffect(() => {
    // keep local copy of activities for optimistic edit/delete
    setLocalActivities(activities || []);
  }, [activities]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(getHiddenPostsStorageKey());
      const parsed = raw ? JSON.parse(raw) : [];
      setHiddenPostIDs(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      setHiddenPostIDs([]);
    }
  }, [viewerId]);

  useEffect(() => {
    if (!ctxCurrentUserID && !propCurrentUserID) return;
    if (progressLoading) return;

    const hasData =
      (activities && activities.length > 0) ||
      (joinedCommunities && joinedCommunities.length > 0) ||
      (uplifts && uplifts.length > 0);

    if (hasData) {
      return;
    }

    if (bootstrapRefreshAttempts.current >= 4) return;

    const nextAttempt = bootstrapRefreshAttempts.current + 1;
    const timer = setTimeout(() => {
      bootstrapRefreshAttempts.current = nextAttempt;
      refresh && refresh();
    }, 900 * nextAttempt);

    return () => clearTimeout(timer);
  }, [
    activities,
    joinedCommunities,
    uplifts,
    refresh,
    ctxCurrentUserID,
    propCurrentUserID,
    progressLoading,
  ]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("selectedCommunity");
      if (saved) {
        const valid =
          saved === "ALL" ||
          saved === "MY_POSTS" ||
          joinedCommunities.some((c) => c.id === saved);
        if (valid) {
          setSelectedCommunity(saved);
        } else if (joinedCommunities && joinedCommunities.length) {
          setSelectedCommunity(joinedCommunities[0].id);
          localStorage.setItem("selectedCommunity", joinedCommunities[0].id);
        } else {
          setSelectedCommunity("ALL");
          localStorage.setItem("selectedCommunity", "ALL");
        }
        // keep the saved selection until user explicitly changes it in Communities
      } else if (
        joinedCommunities &&
        joinedCommunities.length &&
        selectedCommunity === "ALL"
      ) {
        setSelectedCommunity(joinedCommunities[0].id);
      }
    } catch (e) {
      /* ignore */
    }
  }, [joinedCommunities, selectedCommunity]);

  // persist selection so transient remounts or refreshes don't lose it
  function handleCommunityChange(value) {
    setSelectedCommunity(value);
    try {
      localStorage.setItem("selectedCommunity", value);
    } catch (e) {
      /* ignore */
    }
  }

  // When user selects "My Posts", ensure the latest full feed is fetched
  // removed MY_POSTS option — no special fetch needed

  /* -------------------------------------------------------
     POST
  -------------------------------------------------------- */

  async function handlePost() {
    if (!text.trim()) return;

    // Enforce schema rule: posts must belong to ONE community
    if (selectedCommunity === "ALL" || selectedCommunity === "MY_POSTS") {
      console.warn("A specific community must be selected to post.");
      return;
    }

    try {
      setIsPosting(true);

      await createActivity({
        text,
        communities: [selectedCommunity],
        visibility,
      });

      setText("");
    } catch (err) {
      console.error("Failed to share community post:", err);
    } finally {
      setIsPosting(false);
    }
  }

  /* -------------------------------------------------------
     FILTER + SORT
  -------------------------------------------------------- */

  const joinedIDs = (joinedCommunities || []).map((c) => c.id);
  const visiblePosts = (localActivities || [])
    .filter((post) => {
      if (!post) return false;
      if (isPostHidden(post.id)) return false;
      // PRIVATE ("Just Me") only visible to the owner
      if (post.visibility === "PRIVATE" && !isViewer(post.userID)) {
        return false;
      }

      // (My posts moved to PersonalProgress Transformation view)

      // ALL: show owner's posts; plus PUBLIC posts in communities the user has joined
      if (selectedCommunity === "ALL") {
        if (isViewer(post.userID)) return true;
        if (post.visibility !== "PUBLIC") return false;
        return (post.communities || []).some((cid) => joinedIDs.includes(cid));
      }

      // Specific community selected: show public posts for that community (owner always sees their own)
      if (post.communities?.includes(selectedCommunity)) {
        if (post.visibility === "PUBLIC") return true;
        return isViewer(post.userID);
      }

      return false;
    })
    .sort((a, b) => {
      const dateDiff =
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime();

      if (dateDiff !== 0) return dateDiff;

      return (b.engagementCount ?? 0) - (a.engagementCount ?? 0);
    });

  /* -------------------------------------------------------
     HELPERS
  -------------------------------------------------------- */

  function resolveCommunityName(communityID) {
    return (
      joinedCommunities.find((c) => c.id === communityID)?.name ?? "Personal"
    );
  }

  function renderMedia(post) {
    const urls = [
      ...(post.imageURLs || []),
      ...(post.videoURLs || []),
      ...(post.documentURLs || []),
    ];

    return urls.map((url) =>
      url.match(/\.(mp4|webm)$/i) ? (
        <video key={url} src={url} controls style={styles.media} />
      ) : (
        <img key={url} src={url} alt="" style={styles.media} />
      ),
    );
  }

  /* -------------------------------------------------------
     EDIT / DELETE HANDLERS
  -------------------------------------------------------- */

  function handleEditStart(post) {
    setEditingPostId(post.id);
    setEditingText(post.text || "");
    setEditingVisibility(post.visibility || "PUBLIC");
  }

  function handleEditCancel() {
    setEditingPostId(null);
    setEditingText("");
    setEditingVisibility("PUBLIC");
  }

  async function handleEditSave(post) {
    if (!post || !post.id) return;

    const updatedFields = {
      text: editingText,
      visibility: editingVisibility,
    };

    // optimistic update
    setLocalActivities((prev) =>
      (prev || []).map((p) =>
        p.id === post.id ? { ...p, ...updatedFields } : p,
      ),
    );

    try {
      await updateActivity({ id: post.id, ...updatedFields });
    } catch (err) {
      console.error("Failed to save edit:", err);
      // best-effort: refresh to restore real state
      refresh && refresh();
    } finally {
      setEditingPostId(null);
      setEditingText("");
      setEditingVisibility("PUBLIC");
    }
  }

  async function handleDelete(post) {
    if (!post || !post.id) return;
    if (!window.confirm("Delete this post? This cannot be undone.")) return;

    const original = localActivities;
    // optimistic remove
    setLocalActivities((prev) => (prev || []).filter((p) => p.id !== post.id));

    try {
      await deleteActivity({ id: post.id });
    } catch (err) {
      console.error("Failed to delete post:", err);
      // restore on failure
      setLocalActivities(original);
    }
  }

  // Optimistic uplift with small visual feedback
  async function handleGiveUplift(post, emoji) {
    if (!post || !post.id) return;
    if (isViewer(post.userID)) return; // prevent self-uplift in UI

    const original = localActivities;

    // optimistic increment
    setLocalActivities((prev) =>
      (prev || []).map((p) =>
        p.id === post.id
          ? { ...p, engagementCount: (p.engagementCount ?? 0) + 1 }
          : p,
      ),
    );

    const bounceKey = `${post.id}_${emoji}`;
    setBouncing((s) => ({ ...s, [bounceKey]: true }));

    try {
      await giveUplift({ postID: post.id, receiverUserID: post.userID, emoji });
    } catch (err) {
      console.error("giveUplift failed:", err);
      // revert optimistic change
      setLocalActivities(original);
    } finally {
      // brief animation then clear that specific key
      setTimeout(
        () =>
          setBouncing((s) => {
            const next = { ...s };
            delete next[bounceKey];
            return next;
          }),
        600,
      );
    }
  }

  async function listAllNotifications(filter, limit = 200) {
    let nextToken = null;
    const items = [];

    do {
      const res = await client.graphql({
        authMode: "userPool",
        query: listNotifications,
        variables: { filter, limit, nextToken },
      });

      const page = res?.data?.listNotifications?.items || [];
      items.push(...page.filter(Boolean));
      nextToken = res?.data?.listNotifications?.nextToken || null;
    } while (nextToken);

    return items;
  }

  async function getCurrentReporterProfile() {
    const [user, attrs] = await Promise.all([
      getCurrentUser(),
      fetchUserAttributes(),
    ]);

    const id =
      user?.userId ||
      user?.username ||
      user?.attributes?.sub ||
      attrs?.sub ||
      viewerId;

    const email = String(attrs?.email || "")
      .trim()
      .toLowerCase();

    const firstName =
      attrs?.given_name || attrs?.name?.split(" ")?.[0] || "Community";

    const lastName =
      attrs?.family_name ||
      attrs?.name?.split(" ")?.slice(1).join(" ") ||
      "Member";

    return {
      id,
      email: email || NOTIFICATION_SENDER.email,
      firstName,
      lastName,
    };
  }

  async function resolveTargetContact(userAliases = []) {
    const aliases = Array.from(
      new Set(
        (userAliases || [])
          .map((v) => (v === null || v === undefined ? "" : String(v).trim()))
          .filter(isUsableAlias),
      ),
    );

    if (aliases.length === 0) {
      return { email: NOTIFICATION_SENDER.email, userId: null, aliases: [] };
    }

    try {
      const res = await client.graphql({
        authMode: "userPool",
        query: listGeneralInformations,
        variables: {
          filter: {
            or: aliases.flatMap((alias) => [
              { userId: { eq: alias } },
              { owner: { eq: alias } },
            ]),
          },
          limit: 50,
        },
      });

      const match = res?.data?.listGeneralInformations?.items?.find(
        (item) => item?.Email,
      );

      const infoAliases = Array.from(
        new Set(
          (res?.data?.listGeneralInformations?.items || [])
            .flatMap((item) => [item?.userId, item?.owner])
            .filter(isUsableAlias),
        ),
      );

      return {
        email:
          String(match?.Email || "")
            .trim()
            .toLowerCase() || NOTIFICATION_SENDER.email,
        userId:
          String(match?.userId || match?.owner || "").trim() || aliases[0],
        aliases: infoAliases,
      };
    } catch {
      try {
        const fallback = await client.graphql({
          authMode: "userPool",
          query: listNotifications,
          variables: {
            filter: {
              or: aliases.map((alias) => ({ TargetUserId: { eq: alias } })),
            },
            limit: 200,
          },
        });

        const match = fallback?.data?.listNotifications?.items?.find(
          (item) => item?.TargetEmail,
        );

        const seenAliases = Array.from(
          new Set(
            (fallback?.data?.listNotifications?.items || [])
              .map((item) => item?.TargetUserId)
              .filter(isUsableAlias),
          ),
        );

        return {
          email:
            String(match?.TargetEmail || "")
              .trim()
              .toLowerCase() || NOTIFICATION_SENDER.email,
          userId: String(match?.TargetUserId || "").trim() || aliases[0],
          aliases: seenAliases,
        };
      } catch {
        return {
          email: NOTIFICATION_SENDER.email,
          userId: aliases[0] || null,
          aliases,
        };
      }
    }
  }

  async function createModerationNotification({
    notificationType,
    targetUserId,
    targetEmail,
    recommendationType,
    recommendationResult,
    recommendationAction,
    recommendationActionFrequency,
    recommendationNote,
    sender,
    status,
  }) {
    const profile = sender || NOTIFICATION_SENDER;
    const today = new Date().toISOString().split("T")[0];

    await client.graphql({
      authMode: "userPool",
      query: createNotifications,
      variables: {
        input: {
          RecommendationType: recommendationType || "Community Moderation",
          RecommendationResult: recommendationResult || null,
          RecommendationAction: recommendationAction || null,
          RecommendationActionFrequency: recommendationActionFrequency || null,
          RecommendationNote: recommendationNote || null,
          RecommendationDate: today,
          SenderId: String(profile.id || NOTIFICATION_SENDER.id),
          SenderFirstName: profile.firstName || NOTIFICATION_SENDER.firstName,
          SenderLastName: profile.lastName || NOTIFICATION_SENDER.lastName,
          SenderEmail:
            String(profile.email || NOTIFICATION_SENDER.email)
              .trim()
              .toLowerCase() || NOTIFICATION_SENDER.email,
          TargetEmail:
            String(targetEmail || NOTIFICATION_SENDER.email)
              .trim()
              .toLowerCase() || NOTIFICATION_SENDER.email,
          TargetUserId: String(targetUserId || ""),
          NotificationType: notificationType,
          Status: status || "pending",
          StatusDate: today,
        },
      },
    });
  }

  async function handleReportPost(post, reportType) {
    if (!post?.id || isViewer(post.userID)) return;

    const reasonByType = {
      [REPORT_TYPES.UNSAFE]:
        "I think this post shows signs that this person or someone else is unsafe.",
      [REPORT_TYPES.HIDE_PERSONAL]:
        "I do not want to see this post for personal reasons.",
      [REPORT_TYPES.HIDE_OFFENSIVE]:
        "I do not want to see this post because it is offensive.",
    };

    const reportReason = reasonByType[reportType];
    if (!reportReason) return;

    try {
      setReportingPostID(post.id);

      const reporter = await getCurrentReporterProfile();
      const communityID = post.communities?.[0] || null;
      const targetAliases = Array.from(
        new Set(
          [post.userID, post.owner]
            .map((v) => (v === null || v === undefined ? "" : String(v).trim()))
            .filter(isUsableAlias),
        ),
      );
      const targetContact = await resolveTargetContact(targetAliases);

      const duplicate = await listAllNotifications({
        and: [
          { NotificationType: { eq: reportType } },
          { SenderId: { eq: String(reporter.id || "") } },
          { TargetUserId: { eq: String(post.userID || "") } },
          { RecommendationResult: { eq: String(post.id || "") } },
        ],
      });

      let createdReport = false;
      if (duplicate.length === 0) {
        await createModerationNotification({
          notificationType: reportType,
          targetUserId: post.userID,
          targetEmail: targetContact.email,
          recommendationType: "Community Report",
          recommendationResult: post.id,
          recommendationAction: reportReason,
          recommendationActionFrequency: communityID,
          recommendationNote: (post.text || "").slice(0, 240),
          sender: reporter,
          status: "logged",
        });
        createdReport = true;
      } else {
        setReportFeedback("Report already submitted for this post.");
      }

      if (createdReport) {
        setReportFeedback("Report successfully sent.");
      }

      if (
        reportType === REPORT_TYPES.HIDE_PERSONAL ||
        reportType === REPORT_TYPES.HIDE_OFFENSIVE
      ) {
        hidePostLocally(post.id);
      }

      setActiveReportPostID(null);
      try {
        await refresh();
      } catch (refreshErr) {
        console.warn("Post-report refresh failed:", refreshErr);
      }
    } catch (err) {
      console.error("Failed to report post:", err);
      alert("Unable to submit report right now. Please try again.");
    } finally {
      setReportingPostID(null);
      setTimeout(() => setReportFeedback(""), 2600);
    }
  }

  /* -------------------------------------------------------
     RENDER
  -------------------------------------------------------- */

  return (
    <div>
      <div style={styles.page}>
        {/* Header */}
        <div style={styles.header}>
          <img
            src={(process.env.PUBLIC_URL || "") + "/Icon-Comm-Blue.png"}
            alt="Logo"
            style={{
              width: "100%",
              maxWidth: 200,
              height: "auto",
              display: "block",
              margin: "20px auto 30px auto",
            }}
          />
          <h2>Community Reflections</h2>
          <p style={styles.subtitle}>
            Share growth. Encourage others. Journal.
          </p>
        </div>

        {/* Your Chosen Communities */}
        {joinedCommunities && joinedCommunities.length > 0 && (
          <div style={styles.communitiesBadgesSection}>
            <p style={styles.communitiesBadgesLabel}>Your Communities:</p>
            <div style={styles.communitiesBadgesContainer}>
              {joinedCommunities.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleCommunityChange(c.id)}
                  style={{
                    ...styles.communityBadge,
                    ...(selectedCommunity === c.id
                      ? styles.communityBadgeSelected
                      : {}),
                  }}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Composer */}
        <div style={styles.composer}>
          <textarea
            placeholder="What did you notice, practice, or learn today?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={styles.textarea}
          />

          <div style={styles.composerRow}>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              style={styles.select}
            >
              <option value="PUBLIC">Community</option>
              <option value="PRIVATE">Just Me</option>
            </select>

            <button
              style={styles.primaryButton}
              onClick={handlePost}
              disabled={isPosting}
            >
              {isPosting ? "Sharing…" : "Share"}
            </button>
          </div>
        </div>

        {/* Shared Transformations carousel removed — rendered inside shared posts */}

        {reportFeedback && (
          <div style={styles.reportFeedback}>{reportFeedback}</div>
        )}

        {/* Feed */}
        <div style={styles.feed}>
          {visiblePosts.map((post) => (
            <div key={post.id} style={styles.postCard}>
              <div style={styles.postHeader}>
                <strong>
                  {post.communities?.[0]
                    ? joinedCommunities.find(
                        (c) => c.id === post.communities[0],
                      )?.name ||
                      persistedMap[post.communities[0]] ||
                      "Community"
                    : "Personal"}
                </strong>
                <div style={styles.postHeaderRight}>
                  <span style={styles.time}>
                    {post.createdAt
                      ? new Date(post.createdAt).toLocaleString()
                      : ""}
                  </span>
                  {!isViewer(post.userID) && (
                    <button
                      onClick={() =>
                        setActiveReportPostID((prev) =>
                          prev === post.id ? null : post.id,
                        )
                      }
                      style={styles.reportHeaderButton}
                      disabled={reportingPostID === post.id}
                      aria-label="Post options"
                      title="Post options"
                    >
                      {reportingPostID === post.id ? "..." : "⋯"}
                    </button>
                  )}
                </div>
              </div>

              {activeReportPostID === post.id && (
                <div style={styles.reportMenu}>
                  <button
                    style={styles.reportMenuItem}
                    onClick={() => handleReportPost(post, REPORT_TYPES.UNSAFE)}
                  >
                    I think this post shows signs that this person or someone
                    else is unsafe
                  </button>
                  <button
                    style={styles.reportMenuItem}
                    onClick={() =>
                      handleReportPost(post, REPORT_TYPES.HIDE_PERSONAL)
                    }
                  >
                    I do not want to see this post for personal reasons (hide)
                  </button>
                  <button
                    style={styles.reportMenuItem}
                    onClick={() =>
                      handleReportPost(post, REPORT_TYPES.HIDE_OFFENSIVE)
                    }
                  >
                    I do not want to see this post because it is offensive
                    (hide)
                  </button>
                </div>
              )}

              <div style={{ fontSize: 12, color: "#333", marginBottom: 8 }}>
                {post.visibility === "PRIVATE" ? "Just Me" : "Community"}
              </div>

              {editingPostId === post.id ? (
                <>
                  <textarea
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    style={{ width: "100%", minHeight: 80, marginBottom: 8 }}
                  />
                  <select
                    value={editingVisibility}
                    onChange={(e) => setEditingVisibility(e.target.value)}
                    style={{ marginBottom: 8 }}
                  >
                    <option value="PUBLIC">Community</option>
                    <option value="PRIVATE">Just Me</option>
                  </select>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => handleEditSave(post)}
                      style={styles.primaryButton}
                    >
                      Save
                    </button>
                    <button
                      onClick={handleEditCancel}
                      style={styles.upliftButton}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {post.actionName === "shareSeasonTree" &&
                  parseSeasonSnapshot(post.seasonSnapshot) ? (
                    <SeasonTreeSnapshot
                      snapshot={parseSeasonSnapshot(post.seasonSnapshot)}
                    />
                  ) : post.actionName === "share" && post.threadID ? (
                    <div style={styles.transformationScroll}>
                      {(() => {
                        let ids = [];
                        try {
                          ids = JSON.parse(post.threadID) || [];
                        } catch (e) {
                          ids = [];
                          if (
                            typeof post.threadID === "string" &&
                            post.threadID.trim()
                          ) {
                            const cleaned = post.threadID
                              .replace(/^[\s[]+|[\s\]]+$/g, "")
                              .replace(/["']/g, "")
                              .trim();
                            if (cleaned) {
                              ids = cleaned
                                .split(",")
                                .map((s) => s.trim())
                                .filter(Boolean);
                            }
                          }
                        }

                        const refPosts = (ids || [])
                          .map(
                            (id) =>
                              (localActivities || []).find(
                                (a) => String(a.id) === String(id),
                              ) ||
                              (activities || []).find(
                                (a) => String(a.id) === String(id),
                              ),
                          )
                          .filter(Boolean);

                        if (!refPosts || refPosts.length === 0) {
                          return (
                            <div style={{ color: Theme.colors.muted }}>
                              (no referenced posts)
                            </div>
                          );
                        }

                        return refPosts.map((rp) => {
                          const mediaUrls = [
                            ...(rp.imageURLs || []),
                            ...(rp.videoURLs || []),
                            ...(rp.documentURLs || []),
                          ];
                          const mediaUrl = mediaUrls[0];
                          const textSnippet = rp.text
                            ? rp.text.split("\n").slice(0, 3).join(" ")
                            : null;

                          return (
                            <div key={rp.id} style={styles.smallCard}>
                              {mediaUrl ? (
                                mediaUrl.match(/\.(mp4|webm)$/i) ? (
                                  <video
                                    src={mediaUrl}
                                    controls
                                    style={styles.smallMedia}
                                  />
                                ) : (
                                  <img
                                    src={mediaUrl}
                                    alt={rp.text ? rp.text.split("\n")[0] : ""}
                                    style={styles.smallMedia}
                                  />
                                )
                              ) : textSnippet ? (
                                <div
                                  style={{
                                    padding: 8,
                                    minHeight: 110,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    textAlign: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 13,
                                      color: Theme.colors.muted,
                                    }}
                                  >
                                    {textSnippet}
                                  </div>
                                </div>
                              ) : (
                                <div style={styles.sharedPlaceholder} />
                              )}

                              {mediaUrl && (
                                <div style={styles.smallText}>
                                  {rp.text
                                    ? rp.text.split("\n")[0]
                                    : "(no text)"}
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  ) : (
                    <>
                      {post.text && (
                        <p style={{ textAlign: "left" }}>{post.text}</p>
                      )}
                      {renderMedia(post)}
                    </>
                  )}
                  <div style={styles.upliftRow}>
                    {UPLIFT_EMOJIS.map(({ emoji, label }) => {
                      const isOwner = isViewer(post.userID);
                      const key = `${post.id}_${emoji}`;
                      const isBouncing = Boolean(bouncing[key]);
                      const count =
                        uplifts.filter(
                          (u) => u.postID === post.id && u.emoji === emoji,
                        ).length || 0;
                      return (
                        <button
                          key={emoji}
                          title={label}
                          disabled={isOwner}
                          aria-pressed={isBouncing}
                          style={{
                            ...styles.upliftButton,
                            transform: isBouncing ? "scale(1.18)" : undefined,
                            transition: "transform 160ms ease",
                          }}
                          onClick={() => handleGiveUplift(post, emoji)}
                        >
                          <span style={{ fontSize: 18 }}>{emoji}</span>
                          <span
                            style={{
                              color: "#999999",
                              marginLeft: 6,
                              fontSize: 12,
                            }}
                          >
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Edit and Delete Buttons at Bottom */}
                  {isViewer(post.userID) && (
                    <div style={styles.postActionsRow}>
                      <button
                        onClick={() => handleEditStart(post)}
                        style={styles.actionButton}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(post)}
                        style={{
                          ...styles.actionButton,
                          ...styles.actionButtonDanger,
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   STYLES
========================================================= */

const styles = {
  page: {
    width: 350,
    minWidth: 300,
    backgroundColor: "white",
    padding: 24,
    borderRadius: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    color: "#333",
  },

  header: { textAlign: "center", marginBottom: 20 },
  subtitle: { color: Theme.colors.muted, fontSize: 14 },

  filterBar: { marginBottom: 12, textAlign: "center" },

  communitiesBadgesSection: {
    marginBottom: 20,
    padding: 12,
    background: "rgba(0, 119, 204, 0.05)",
    borderRadius: Theme.radiusLg,
    textAlign: "left",
  },

  communitiesBadgesLabel: {
    fontSize: 12,
    color: Theme.colors.muted,
    fontWeight: 600,
    marginBottom: 8,
    marginTop: 0,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  communitiesBadgesContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },

  communityBadge: {
    padding: "6px 14px",
    borderRadius: Theme.radiusFull,
    border: `2px solid ${Theme.colors.border}`,
    background: "white",
    color: "#333",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    transition: "all 0.2s ease",
  },

  communityBadgeSelected: {
    background: Theme.colors.primary,
    color: "white",
    borderColor: Theme.colors.primary,
  },

  select: {
    padding: "10px 14px",
    borderRadius: Theme.radiusFull,
    border: `1px solid ${Theme.colors.border}`,
  },

  composer: {
    background: Theme.colors.card,
    borderRadius: Theme.radiusLg,
    padding: 16,
    marginBottom: 24,
    boxShadow: Theme.shadow.sm,
    textAlign: "left",
  },

  textarea: {
    width: "100%",
    borderRadius: Theme.radius,
    padding: 12,
    minHeight: 90,
    border: `1px solid ${Theme.colors.border}`,
  },

  composerRow: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 12,
    gap: 12,
  },

  primaryButton: {
    borderRadius: Theme.radiusFull,
    padding: "8px 18px",
    background: Theme.colors.primary,
    color: "#fff",
    border: "none",
    cursor: "pointer",
  },

  feed: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },

  postCard: {
    background: Theme.colors.card,
    borderRadius: Theme.radiusLg,
    padding: 16,
    boxShadow: Theme.shadow.sm,
    textAlign: "left",
  },

  postHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
  },

  postHeaderRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 6,
  },

  time: {
    fontSize: 12,
    color: Theme.colors.muted,
  },

  upliftRow: {
    display: "flex",
    gap: 8,
    marginTop: 8,
  },

  upliftButton: {
    borderRadius: Theme.radiusFull,
    border: `1px solid ${Theme.colors.border}`,
    background: Theme.colors.highlight,
    padding: "6px 12px",
    cursor: "pointer",
  },

  postActionsRow: {
    gap: 8,
    marginTop: 12,
    paddingTop: 12,
    borderTop: `1px solid ${Theme.colors.border}`,
    justifyContent: "flex-end",
  },

  actionButton: {
    borderRadius: Theme.radiusFull,
    border: `1px solid ${Theme.colors.border}`,
    background: "white",
    color: "#333",
    padding: "6px 14px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    marginRight: "10px",
  },

  actionButtonDanger: {
    borderColor: "#ff4d4d",
    color: "#ff4d4d",
  },

  reportHeaderButton: {
    borderRadius: Theme.radius,
    border: `1px solid ${Theme.colors.border}`,
    background: "#fff",
    color: Theme.colors.muted,
    padding: "2px 8px",
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
    fontWeight: 700,
  },

  reportMenu: {
    marginTop: 8,
    marginBottom: 8,
    border: `1px solid ${Theme.colors.border}`,
    borderRadius: Theme.radius,
    overflow: "hidden",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
  },

  reportMenuItem: {
    border: "none",
    background: "#fff",
    textAlign: "left",
    padding: "10px 12px",
    cursor: "pointer",
    fontSize: 12,
    borderBottom: `1px solid ${Theme.colors.border}`,
  },

  reportFeedback: {
    marginBottom: 12,
    padding: "10px 12px",
    borderRadius: Theme.radius,
    background: "#eaf7ef",
    border: "1px solid #b7e4c7",
    color: "#1f6f43",
    fontSize: 13,
    fontWeight: 600,
  },

  media: {
    width: "100%",
    borderRadius: Theme.radius,
    marginTop: 8,
  },
  sharedRow: {
    display: "flex",
    gap: 12,
    overflowX: "auto",
    paddingBottom: 12,
    WebkitOverflowScrolling: "touch",
    msOverflowStyle: "auto",
    marginBottom: 16,
  },
  sharedCard: {
    flex: "0 0 220px",
    background: Theme.colors.card,
    borderRadius: Theme.radiusLg,
    padding: 8,
    boxShadow: Theme.shadow.sm,
    textAlign: "center",
  },
  sharedMedia: {
    width: "100%",
    height: 140,
    objectFit: "cover",
    borderRadius: Theme.radius,
    display: "block",
  },
  sharedPlaceholder: {
    width: "100%",
    height: 140,
    background: Theme.colors.border,
    borderRadius: Theme.radius,
  },
  sharedText: {
    marginTop: 8,
    fontSize: 12,
    color: Theme.colors.muted,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  transformationScroll: {
    display: "flex",
    gap: 12,
    overflowX: "auto",
    padding: "8px 0",
    WebkitOverflowScrolling: "touch",
  },
  smallCard: {
    flex: "0 0 200px",
    background: Theme.colors.card,
    borderRadius: Theme.radiusLg,
    padding: 8,
    boxShadow: Theme.shadow.sm,
    textAlign: "left",
  },
  smallMedia: {
    width: "100%",
    height: 110,
    objectFit: "cover",
    borderRadius: Theme.radius,
    display: "block",
  },
  smallText: {
    marginTop: 8,
    fontSize: 12,
    color: Theme.colors.muted,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};
